import {IMuWidget} from "./IMuWidget";
import {CourseCL} from "./generated/CourseCL";
import {CourseFull, Slide, VersionPart} from "./CourseTypes";
import {formatDateTime} from "./Utils";

export class Course extends IMuWidget
{
	private slideCount: number;

	private currentSlideNum: number = 0;

	public cl: CourseCL;

	public clUrl: string;

	public place: string;

	public forceCompletion: boolean = true;

	private anyNotCompleted: boolean = true;

	public editUrl: string|null = null;

	public isPreview: boolean = false;

	beforeIndex() {
		this.container.innerHTML = `
			<span mu-id="bOpenCourse" class="course__open-course">
				<span class="icon-book-help"></span>
				<span mu-id="info"></span>
				<span mu-id="icon"></span>				
			</span>
			<div class="course_view" style="display: none" mu-id="details">
				<div mu-id="slides" mu-bind="slides:@foreach" class="course_view__slides">
					<div class="course_view__slide" mu-template="slide" mu-widget="CourseSlide">
						<div class="course_view__slide_course_info">
							<strong mu-bind="courseName"></strong>
							<span mu-bind="courseVersion" title="Aktuálně platná verze školení"></span> /
							<span mu-bind="completeVersion" title="Poslední přečtená a odsouhlasená verze školení"></span> 
							<span mu-bind='statusIcon|prepend("icon-"):className'></span>
						</div>
						<h2 mu-id="name" mu-bind="name"></h2>
						<div mu-id="content" mu-bind="content:innerHTML"></div>
						<div mu-id="pConfirm" class="course_view__confirm">
							<label mu-for="confirmCheckbox">
								<input type="checkbox" mu-id="confirmCheckbox">
								Přečetl jsem, rozumím a budu dodržovat.
							</label>
							<div>
								<span class="btn-primary btn btn-lg" mu-id="bConfirm">Potvrzuji</span>
							</div>
							<div>
								<em mu-id="dtCompletion"></em>
							</div>
						</div>
					</div>
				</div>
				<span class="course_view__btn_close" mu-id="bClose">&times;</span>
				<span class="course_view__btn_prev" mu-id="bPrev">&lt;</span>
				<span class="course_view__btn_next" mu-id="bNext">&gt;</span>`
			+ (this.editUrl ? '<a class="course_view__btn_edit" mu-id="bEdit">Upravit školení</a>' : '')
			+ `</div>`;
		this.container.classList.add('course');
	}

	override muBindData(srcData: any) {
		super.muBindData(srcData);
		if (srcData.slides) this.slideCount = srcData.slides.length;
	}

	afterIndex() {
		if (this.isPreview) {
			this.muVisible(false, 'bOpenCourse');
		} else {
			this.cl = new CourseCL(this.clUrl);
			this.loadCurses();
			if (this.editUrl) this.ui.bEdit.href = this.editUrl;
		}
	}

	preview(slides: Slide[]) {
		this.muBindData({
			slides: slides
		});
		this.muVisible(true, "details");
		this.switchSlide(0);
	}

	loadCurses() {
		this.cl.getCoursesFor(this.place).then(data => {
			this.anyNotCompleted = data.some(c => c.diffPart === 'major');
			const byPart: Record<VersionPart|"ok", number> = {
				major: 0,
				minor: 0,
				patch: 0,
				ok: 0
			};
			data.forEach(course => byPart[course.diffPart ?? "ok"]++);
			let icon = '';
			let info = "";
			const count = data.length;
			if (byPart.major) {
				icon = 'warning';
				info = "Splněno " + (count - byPart.major).toString() + " z " + count.toString();
			}
			else if (byPart.minor) {
				icon = 'info-g';
				info = "Splněno vše"
			}
			else if (count == 0) {
				info = "Žádné školení"
			}
			else
			{
				icon = 'ok';
				info = "Splněno vše"
			}

			this.ui.info.innerText = info;
			this.ui.icon.className = "icon-" + icon;

			const slideData = [];
			for(const course of data) {
				const courseMetadata = {
					id: course.id,
					courseName: course.name,
					courseVersion: course.version,
					completeVersion: course.completeVersion,
					statusIcon: course.statusIcon,
					dtCompletion: course.dtCompletion ? new Date(course.dtCompletion) : null,
				};
				for(const slide of course.content.slides) {
					slideData.push({
						...courseMetadata,
						name: slide.name,
						content: slide.content,
						showConfirm: false
					})
				}
				slideData.push({
					...courseMetadata,
					name: 'Potvrzení',
					showConfirm: true
				})
			}
			this.muBindData({
				slides: slideData
			});
			this.switchSlide(this.currentSlideNum);
			if (this.forceCompletion && this.anyNotCompleted) this.bOpenCourse_click();
		})
	}

	bPrev_click() {
		this.switchSlide('prev');
	}
	bNext_click() {
		this.switchSlide('next');
	}

	private switchSlide(slideNum: 'prev'|'next'|number) {
		if (slideNum === 'next') slideNum = Math.min(this.currentSlideNum + 1, this.slideCount - 1);
		if (slideNum === 'prev') slideNum = Math.max(this.currentSlideNum - 1, 0);
		let i = 0;
		this.currentSlideNum = slideNum;
		for(const slide of this.muGetChildWidgets<CourseSlide>('slides')) {
			const cl = slide.container.classList;
			cl.toggle('course_view__slide--pre', i < this.currentSlideNum);
			cl.toggle('course_view__slide--post', i > this.currentSlideNum);
			i++;
		}
	}

	bOpenCourse_click() {
		this.muVisible("toggle", "details");
	}

	bClose_click() {
		this.muVisible("toggle", "details");
	}
}

export class CourseSlide extends IMuWidget
{
	private courseId: number;

	muParent: Course;

	override muBindData(srcData: CourseFull|any) {
		super.muBindData(srcData);
		this.muVisible(srcData.showConfirm, 'pConfirm');
		if (srcData.showConfirm) {
			const completion = !!srcData.dtCompletion;
			this.ui.confirmCheckbox.disabled = completion;
			this.ui.confirmCheckbox.checked = completion;
			if (completion) {
				this.ui.dtCompletion.innerText = 'Potvrzeno ' + formatDateTime(srcData.dtCompletion);
			}
		}
		this.courseId = srcData.id;
	}

	bConfirm_click() {
		if (this.ui.confirmCheckbox.checked) {
			this.muParent.cl.confirmCourseCompletion(this.courseId)
				.then(() => this.muParent.loadCurses())
		} else alert('Potrďte zakliknutím');
	}
}
